import { getAccessTokenAsync } from './auth';

// Cache-Manager für Multi-Tenancy
class CacheManager {
  private currentOrgID: number | null = null;

  /**
   * Prüft ob sich die Organisation geändert hat und löscht den Cache bei Bedarf
   */
  async checkAndClearCacheOnOrgChange(): Promise<void> {
    try {
      // Prüfe sowohl JWT-Token als auch localStorage selectedOrgID
      const accessToken = await getAccessTokenAsync();
      let newOrgID: number | null = null;
      
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          newOrgID = payload.orgID;
        } catch (error) {
          console.error('Fehler beim Parsen des JWT-Tokens:', error);
        }
      }

      // Prüfe auch den selectedOrgID aus localStorage
      const selectedOrgID = localStorage.getItem('selectedOrgID');
      if (selectedOrgID) {
        const parsedSelectedOrgID = parseInt(selectedOrgID);
        if (!isNaN(parsedSelectedOrgID)) {
          // Verwende den selectedOrgID aus localStorage, da dieser die aktuelle Auswahl ist
          newOrgID = parsedSelectedOrgID;
          console.log('🏢 Cache-Manager: Verwende selectedOrgID aus localStorage:', newOrgID);
        }
      }

      if (!newOrgID) {
        this.currentOrgID = null;
        return;
      }

      // Wenn sich die Organisation geändert hat, Cache löschen
      if (this.currentOrgID !== null && this.currentOrgID !== newOrgID) {
        console.log('🔄 Organisation gewechselt von', this.currentOrgID, 'zu', newOrgID, '- lösche Cache...');
        this.clearAllCaches();
      }

      this.currentOrgID = newOrgID;
    } catch (error) {
      console.error('Fehler beim Cache-Check:', error);
    }
  }

  /**
   * Löscht alle Caches
   */
  private clearAllCaches(): void {
    // localStorage Caches löschen
    localStorage.removeItem('locationsWithIds');
    localStorage.removeItem('standardStandorte');
    localStorage.removeItem('radioChannelsWithIds');
    localStorage.removeItem('standardFunkkanaele');
    localStorage.removeItem('operationsTypes');
    localStorage.removeItem('tasks');
    
    // sessionStorage Caches löschen
    sessionStorage.removeItem('locationsWithIds');
    sessionStorage.removeItem('standardStandorte');
    sessionStorage.removeItem('radioChannelsWithIds');
    sessionStorage.removeItem('standardFunkkanaele');
    sessionStorage.removeItem('operationsTypes');
    sessionStorage.removeItem('tasks');

    // Auch die syncThrottled Caches löschen
    localStorage.removeItem('operationsTypesCache');
    sessionStorage.removeItem('operationsTypesCache');

    console.log('✅ Alle Caches gelöscht');
  }

  /**
   * Setzt die aktuelle Organisation (für manuelle Cache-Invalidierung)
   */
  setCurrentOrgID(orgID: number): void {
    // Wenn sich die Organisation geändert hat, Cache sofort löschen
    if (this.currentOrgID !== null && this.currentOrgID !== orgID) {
      console.log('🔄 Organisation manuell gewechselt von', this.currentOrgID, 'zu', orgID, '- lösche Cache sofort...');
      this.clearAllCaches();
    }
    
    this.currentOrgID = orgID;
  }

  /**
   * Löscht den Cache sofort (für manuelle Aufrufe)
   */
  forceClearCache(): void {
    console.log('🧹 Cache wird manuell gelöscht...');
    this.clearAllCaches();
  }

  /**
   * Gibt die aktuelle Organisation zurück
   */
  getCurrentOrgID(): number | null {
    return this.currentOrgID;
  }
}

// Singleton-Instanz
const cacheManager = new CacheManager();

export const getCacheManager = (): CacheManager => {
  return cacheManager;
};

// Cache bei Organisationswechsel löschen
export const clearCacheOnOrgChange = (): Promise<void> => {
  return cacheManager.checkAndClearCacheOnOrgChange();
};
